import React, { Component } from "reactn";
import styled from "styled-components";
import { app } from "js/namespaces";
import { FeatureType } from "common/features";
import { Fab, Icon } from "@material-ui/core";
import { _ } from "js/vendor";
import { UIPane } from "js/react/components/UiComponents";
import { SearchThrottleWait } from "common/constants";
import {
    SlideTemplatesByCategoryList,
    SlideTemplateSearchPreview,
} from "js/react/views/AddSlide/Panes/SlideTemplatesPane";
import { SlideGalleryPreviewThumbnail } from "js/react/views/AddSlide/Panes/SlideGalleryPane";
import { TeamSlideThumbnail } from "js/react/views/AddSlide/Panes/TeamSlideLibraryPane";
import { CopySlideThumbnail } from "js/react/views/AddSlide/Panes/CopySlidePane";
import { TemplateInfoPane } from "js/react/views/AddSlide/Panes/TemplateInfoPane";
import Spinner from "js/react/components/Spinner";
import { Gap10 } from "js/react/components/Gap";
import {
    gallerySlidesDataService,
    slideTemplatesDataService,
    userSlidesDataService,
    teamSlidesDataService
} from "js/react/views/AddSlide/DataServices";
import { SlideSearchPreview } from "./Components/SlideSearchPreview";
import {
    SearchBarContainer,
    SearchBarInnerContainer,
    UIPaneResultsContainer,
    SlideSearchInput
} from "./Components/SearchBox";
import { UIActionType, isUIActionRestricted } from "js/core/uiActionsRestrictors";
import { NoMatchTemplatesNotice } from "js/react/components/Notice";
import { appVersion } from "js/config";
import { getCanvasBundle } from "js/canvas";
import { openSuggestTemplateForm } from "js/core/utilities/externalLinks";

export const SearchPreview = styled.div`
  background: white;
  padding: 20px;
  margin: 0px 0px 20px;
`;
export const SearchPreviewHeader = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-left: 20px;
  margin-bottom: 24px;
  color: #111;
  display: flex;
  align-items: center;
`;

export class SearchSlidesPane extends Component {
    state = {
        query: "",
        isTemplateResultsLoaded: false,
        isGalleryResultsLoaded: false,
        isTeamSlideResultsLoaded: false,
        isCopySlideResultsLoaded: false,
        isSlideTemplatesLoaded: false,
        slideTemplateResults: [],
        slideGalleryResults: [],
        teamSlideResults: [],
        copySlideResults: []
    }

    constructor() {
        super();

        this.slideTemplates = null;

        this.searchBarRef = React.createRef();
        this.scrollRef = React.createRef();
        this.doSearchThrottled = _.throttle(this.doSearch, SearchThrottleWait, { leading: false });
    }

    componentDidMount() {
        this.doSearch();
        this.loadSlideTemplates();
    }

    get areAllSearchResultsLoaded() {
        const { organizationId } = this.props;
        const { isTemplateResultsLoaded, isCopySlideResultsLoaded, isGalleryResultsLoaded, isTeamSlideResultsLoaded, isSlideTemplatesLoaded } = this.state;
        const areAllResultsLoaded = isTemplateResultsLoaded && isCopySlideResultsLoaded && isGalleryResultsLoaded && isSlideTemplatesLoaded;

        if (organizationId) {
            return areAllResultsLoaded && isTeamSlideResultsLoaded;
        }

        return areAllResultsLoaded;
    }

    async loadSlideTemplates() {
        const { slideTemplates: { slideTemplates } } = await getCanvasBundle(appVersion);
        this.slideTemplates = slideTemplates;
        this.setState({ isSlideTemplatesLoaded: true });
    }

    handleSearch = query => {
        this.setState({
            query: query,
            isTemplateResultsLoaded: false,
            isCopySlideResultsLoaded: false,
            isGalleryResultsLoaded: false,
            isTeamSlideResultsLoaded: false
        }, () => this.doSearchThrottled());
    }

    doSearch = async () => {
        const { organizationId } = this.props;
        const { query } = this.state;

        if (!query) {
            this.setState({
                slideTemplateResults: [],
                slideGalleryResults: [],
                teamSlideResults: [],
                copySlideResults: [],
                isTemplateResultsLoaded: true,
                isCopySlideResultsLoaded: true,
                isGalleryResultsLoaded: true,
                isTeamSlideResultsLoaded: true,
            });
            return;
        }

        const fullTextSearchQuery = query;
        const size = 8;
        const minScore = 1;
        const [slideTemplateResults, copySlideResults, teamSlideResults, slideGalleryResults] = await Promise.all([
            slideTemplatesDataService.search({ fullTextSearchQuery, size }),
            userSlidesDataService.search({ fullTextSearchQuery, size, minScore, organizationId }),
            organizationId ? teamSlidesDataService.search({ fullTextSearchQuery, size, minScore, organizationId }) : Promise.resolve([]),
            gallerySlidesDataService.search({ fullTextSearchQuery, size, minScore })
        ]);

        this.setState({
            slideTemplateResults,
            slideGalleryResults,
            teamSlideResults,
            copySlideResults,
            isTemplateResultsLoaded: true,
            isCopySlideResultsLoaded: true,
            isGalleryResultsLoaded: true,
            isTeamSlideResultsLoaded: true,
        });
    }

    handleClearSearch = () => {
        this.searchBarRef.current.updateQuery("");
    }

    handleSelectTemplate = ({ id: templateId }) => {
        const { onTemplateSelected } = this.props;

        const template = this.slideTemplates[templateId];
        if (
            !app.user.features.isFeatureEnabled(FeatureType.WORKSPACE_CAN_ACCESS_INSPIRATION_SLIDES, app.appState.workspaceId) ||
            templateId === "slidePlaceholder"
        ) {
            onTemplateSelected(template);
        } else {
            this.setState({ selectedTemplate: template });
        }
    }

    handleSelectViewAll = pane => {
        const { onSelectViewAll } = this.props;
        const { query } = this.state;

        this.setGlobal({ searchSlidesQuery: query });
        onSelectViewAll(pane);
    }

    renderSlides = () => {
        const { query, slideTemplateResults, slideGalleryResults, teamSlideResults, copySlideResults } = this.state;

        if (!this.areAllSearchResultsLoaded) {
            return <Spinner />;
        }

        const slideTemplateItems = slideTemplateResults.filter(({ id }) => !isUIActionRestricted(UIActionType.ADD_SLIDE, { templateId: id }));
        const slideGalleryItems = slideGalleryResults.filter(({ template_id }) => !isUIActionRestricted(UIActionType.ADD_SLIDE, { templateId: template_id }));
        const teamSlideItems = teamSlideResults.filter(({ templateId }) => !isUIActionRestricted(UIActionType.ADD_SLIDE, { templateId }));
        const copySlideItems = copySlideResults.filter(({ templateId }) => !isUIActionRestricted(UIActionType.ADD_SLIDE, { templateId }));

        const hasNoMatches = slideTemplateItems.length === 0 &&
            slideGalleryItems.length === 0 &&
            teamSlideItems.length === 0 &&
            copySlideItems.length === 0;

        if (query.length > 0 && this.areAllSearchResultsLoaded && hasNoMatches) {
            return (
                <NoMatchTemplatesNotice
                    onAddClassicSlide={this.props.onAddBlankClassicSlide}
                />
            );
        }

        if (query.length > 0) {
            return (
                <div className="search-results-frame">
                    {slideTemplateItems.length > 0 &&
                        <SlideTemplateSearchPreview
                            items={slideTemplateItems}
                            onSelected={this.handleSelectTemplate}
                            showUserTheme={this.props.showUserTheme}
                            onToggleUserTheme={this.props.onToggleUserTheme}
                        />
                    }
                    {
                        app.user.features.isFeatureEnabled(FeatureType.WORKSPACE_CAN_ACCESS_INSPIRATION_SLIDES, app.appState.workspaceId) &&
                        slideGalleryItems.length > 0 &&
                        <SlideSearchPreview
                            title={"Start from our Inspiration Gallery"}
                            thumbnail={SlideGalleryPreviewThumbnail}
                            items={slideGalleryItems}
                            onSelected={this.props.onGallerySlideSelected}
                            onSelectViewAll={() => this.handleSelectViewAll("slide-gallery")}
                            showUserTheme={this.props.showUserTheme}
                            onToggleUserTheme={this.props.onToggleUserTheme}
                        />
                    }
                    {teamSlideItems.length > 0 &&
                        <SlideSearchPreview
                            title={"Start from Team Slides"}
                            thumbnail={TeamSlideThumbnail}
                            items={teamSlideItems}
                            onSelected={this.props.onTeamSlideSelected}
                            onSelectViewAll={() => this.handleSelectViewAll("slide-library")}
                            hideUseCurrentThemeSwitch={true}
                        />
                    }
                    {copySlideItems.length > 0 &&
                        <SlideSearchPreview
                            title={"Start from your Slides"}
                            thumbnail={CopySlideThumbnail}
                            items={copySlideItems}
                            onSelected={slide => this.props.onCopySlideSelected([slide])}
                            onSelectViewAll={() => this.handleSelectViewAll("copy-slide")}
                            hideUseCurrentThemeSwitch={true}
                        />
                    }
                </div>
            );
        }
    }

    render() {
        const { query, selectedTemplate } = this.state;

        if (selectedTemplate) {
            return (
                <TemplateInfoPane
                    template={selectedTemplate}
                    onTemplateSelected={this.props.onTemplateSelected}
                    onClassicTemplateSelected={this.props.onClassicTemplateSelected}
                    onGallerySlideSelected={this.props.onGallerySlideSelected}
                    showUserTheme={this.props.showUserTheme}
                    onToggleUserTheme={this.props.onToggleUserTheme}
                />
            );
        }

        return (
            <UIPane>
                <SearchBarContainer>
                    <SearchBarInnerContainer>
                        <SlideSearchInput
                            query={query}
                            ref={this.searchBarRef}
                            onSearch={this.handleSearch}
                            placeholder="Search for a slide..."
                            showShortCuts
                        />
                    </SearchBarInnerContainer>
                </SearchBarContainer>
                <UIPaneResultsContainer>
                    {query.length == 0 &&
                        <div className="slide-templates-list">
                            <SlideTemplatesByCategoryList
                                onSelected={this.handleSelectTemplate}
                                hideUseCurrentThemeSwitch={this.props.hideUseCurrentThemeSwitch}
                                showUserTheme={this.props.showUserTheme}
                                onToggleUserTheme={this.props.onToggleUserTheme}
                            />
                        </div>
                    }
                    {this.renderSlides()}
                </UIPaneResultsContainer>
                <Fab
                    variant="extended"
                    className="floating-fab expanding-fab"
                    onClick={openSuggestTemplateForm}
                >
                    <Icon className="text-blue">how_to_vote</Icon>
                    <Gap10 />
                    <span>Suggest a template</span>
                </Fab>
            </UIPane>
        );
    }
}
